import ArticleContainer from '@/components/article-container';
import React, { useEffect } from 'react';
import { useHistory } from '@jupiter/plugin-runtime/router';
import qs from 'querystring';
import {
  AttachmentDetail,
  LearningInfo,
  RoleType,
} from '@/api/seller/serv/oec_seller_learning_center_api';
import styles from './index.scss';
import { ArticleRateComment } from '@features/components/article-rate-comment';
import NoContentPage from '@features/components/no-content-page';
import { useContextUser } from '@features/hooks';
import { Tea } from '@features/libs/tea';
import TagLimitLine from '@features/components/tag-limit-line';
import useTransViewCount from '@/hooks/useTransViewCount';
import { useTranslation } from '@jupiter-app/plugin-i18n';
import { getQuery } from '@i18n-ecom/lib/dist/commonjs/url';
import { useI18nTimeFormat } from '@/hooks/useI18nTimeFormat';
import { isModulesPage } from '@/libs/url';
import { useUserModel } from '@/models';
import Player, { Events } from 'xgplayer';
import {
  reportClickEvent,
  reportVideoDurationEvent,
  reportVideoShowEvent,
} from '@/containers/essay-detail/reports';

interface CourseInsideProps {
  className?: string;
  name?: string;
  courseDetail?: AttachmentDetail;
  contentId?: number;
  courseInfo?: LearningInfo;
  reportObj?: object;
}

const CourseDisplay: React.FC<CourseInsideProps> = React.memo(props => {
  const history = useHistory();
  const { className, name, courseDetail, contentId, courseInfo, reportObj } =
    props;
  const { t } = useTranslation();
  const [user] = useContextUser();
  const { tFormat } = useI18nTimeFormat();
  const { isCreator, isSeller } = useUserModel();
  const params = {
    page_name: 'content_detail',
    type: 'page',
    is_login: !!user.user_id,
    url: window.location.href,
    prev_page_name: window.document.referrer,
    content_type: 'course',
    content_module: isModulesPage() ? 'modules' : '',
    identity_type: isCreator ? 'creator' : isSeller ? 'seller' : '',
    learning_id: courseInfo?.learning_id,
    course_id: courseInfo?.learning_id,
    course_name: courseInfo?.name,
    article_id: contentId,
    article_name: name,
    video_id: contentId,
    video_name: name,
    is_video: true,
  };

  const initPlayer = (url: string) => {
    let isPlayOnce = false;
    const playReportObj = {
      ...(reportObj ?? params),
      video_id: courseDetail?.file_name || courseDetail?.file_key || contentId,
    };

    const player = new Player({
      id: `course_video_${contentId}`,
      url,
      autoplay: false,
      volume: 0.3,
      lang: 'en',
      closeFocusVideoFocus: true,
    });

    player.on(Events.ENDED, () => {
      Tea.pageViewFinish(reportObj ?? params);
    });

    player.on(Events.PLAY, () => {
      if (!isPlayOnce) {
        isPlayOnce = true;
        reportVideoShowEvent(playReportObj);
      }

      reportClickEvent('play', playReportObj);
    });

    return player;
  };

  useEffect(() => {
    const { channel = '', ...search } = getQuery(location.search);

    if (channel) {
      history.replace({ search: qs.stringify(search as any) });
    }

    const startTimeMs = new Date().getTime();
    Tea('page_show', Object.assign({}, reportObj ?? params, { channel }));

    let player: Player | null;
    if (courseDetail?.file_key) {
      player = initPlayer(courseDetail?.file_key);
    }

    const reportDuration = () => {
      Tea.pageDuration(
        Object.assign(
          {
            duration: new Date().getTime() - startTimeMs,
          },
          reportObj ?? params,
        ),
      );
      if (player) {
        reportVideoDurationEvent(player.cumulateTime * 1000, {
          ...(reportObj ?? params),
          video_id:
            courseDetail?.file_name || courseDetail?.file_key || contentId,
        });
      }
    };

    window.addEventListener('beforeunload', reportDuration);

    return () => {
      reportDuration();
      if (player) {
        player.destroy();
        player.offAll();
        player = null;
      }
      window.removeEventListener('beforeunload', reportDuration);
    };
  }, [contentId]);

  return (
    <ArticleContainer className={className} breadCrumbClassName="h5:hidden">
      <div className={styles.articleLayout}>
        <section className="flex flex-col flex-1">
          {name && (
            <div className="text-gray-1 font-semibold text-24">{name}</div>
          )}
          <div className={styles.courseDes}>
            {courseInfo?.create_time && (
              <span className={styles.timeStyle}>
                {tFormat(
                  courseInfo?.modify_time ?? courseInfo?.create_time,
                  'L',
                )}
              </span>
            )}
            {/* {typeof courseInfo?.views === 'number' && (
              <span>
                {courseInfo?.views > 1
                  ? useTransViewCount(courseInfo?.views)
                  : '--'}
                {` ${t('academy_homepage_content_cover_views')}`}
              </span>
            )} */}
          </div>

          {courseInfo?.tags && <TagLimitLine tagList={courseInfo?.tags} />}

          {courseDetail?.file_key ? (
            <>
              <div className={styles.articleRender}>
                <div
                  className={styles.videoWrapper}
                  id={`course_video_${contentId}`}
                />
              </div>
              <ArticleRateComment contentId={contentId} name={name} />
            </>
          ) : (
            <NoContentPage className="mx-auto" />
          )}
        </section>
      </div>
    </ArticleContainer>
  );
});

export default CourseDisplay;
